import { PageHeader } from '@/components/PageHeader/PageHeader.tsx'
import { ChargeTerminalCounters } from '@/features/charging/components/layout/ChargeTerminalCounters'
import { Box, Stack, Typography } from '@mui/material'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCompany } from '@/contexts/CompaniesContext'
import SiteAccordion from '@/features/charging/components/layout/SiteAccordion'
import { useFilteredAllowedSites } from '@/hooks/useFilteredAllowedSites'
import { useSiteFilterShape } from '@/hooks/useSiteFilterShape.ts'
import { z } from 'zod'
import { useFormBuilderSearch } from '@/components/FormBuilder/formBuilderSearch.ts'
import { PageSearchFilter } from '@/components/PageSearchFilter/PageSearchFilter.tsx'

export const Route = createFileRoute(
  '/_authenticated/$companySlug/charging-infrastructure/'
)({
  beforeLoad: ({ context }) => {
    context.auth.rejectIfMissingPermissions(['CHARGER_READ'])
  },
  component: AllChargeTerminals,
  head: () => ({
    meta: [{ title: 'List all charge terminals' }]
  })
})

function AllChargeTerminals() {
  const [searchQuery, setSearchQuery] = useState('')
  const { t } = useTranslation(['_routes', '_global'])
  const { sites: desiredSiteIds } = Route.useSearch()
  const { id: companyId, sites } = useCompany()
  const { selectedSitesIds, filteredSites } = useFilteredAllowedSites(
    sites,
    desiredSiteIds
  )

  const siteFilterShape = useSiteFilterShape()
  const filterSchema = useMemo(() => {
    return z.object({ ...siteFilterShape })
  }, [siteFilterShape])
  const { persistOnSubmit } = useFormBuilderSearch(filterSchema)

  return (
    <Stack spacing={4}>
      <PageHeader>{t('routes.$siteId_terminals.main.mainTitle')}</PageHeader>
      <PageSearchFilter
        textFilter={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: t('routes.$siteId_terminals.main.searchPlaceholder')
        }}
        buttonFilter={{
          schema: filterSchema,
          onSubmit: persistOnSubmit(),
          defaultValues: { sites: selectedSitesIds }
        }}
      />
      <ChargeTerminalCounters
        companyId={companyId}
        siteIds={filteredSites.map(site => site.id)}
      />
      {filteredSites.length > 0 ? (
        filteredSites.map(siteElement => (
          <SiteAccordion
            key={siteElement.id}
            companyId={companyId}
            siteElement={siteElement}
          />
        ))
      ) : (
        <Box
          sx={{
            mt: 4,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Typography variant="subtitle4" color="text.secondary">
            {t('routes.$siteId_terminals.errors.noSitesMessage')}
          </Typography>
        </Box>
      )}
    </Stack>
  )
}
